import { useMemo, useState } from 'react'
import { useApp } from '../state/AppStore'
import { useAllEvents } from '../state/useAllEvents'
import { Stats } from './Stats'
import { StatGrid } from '../components/StatGrid'
import { orderStatTypes } from '../domain/statTypes'
import { tally, teamTotal } from '../domain/derive'
import { formatDate } from '../lib/date'
import type { StatTypeId } from '../domain/types'

/**
 * The signed-out staging experience (task 04, staging only).
 *
 * Teammates can look around with no sign-in friction: the real Stats screen
 * (which also opens Season So Far and the per-game recaps), and a read-only
 * demo of the live grid. Everything is read-only -- there is no New game, no
 * Manage, no Admin, and the database refuses any write from an anonymous
 * session regardless. Sign-in is one tap away for anyone who wants to try it.
 */
export function GuestApp({ onSignIn }: { onSignIn: () => void }) {
  const { snapshot } = useApp()
  const [view, setView] = useState<'stats' | 'live'>('stats')
  const team = snapshot?.snapshot.teams.find((t) => t.is_us)?.name ?? 'Firehawks'

  return (
    <div className="app">
      <header className="app__header">
        <div className="app__brand">
          <span className="app__logo" aria-hidden="true" />
          <span className="app__brand-app">Hustle Stats:</span>
          <span className="app__brand-team">{team}</span>
        </div>
        <nav className="tabs">
          <button type="button" className={'tabs__btn' + (view === 'stats' ? ' tabs__btn--on' : '')} onClick={() => setView('stats')}>
            Stats
          </button>
          <button type="button" className={'tabs__btn' + (view === 'live' ? ' tabs__btn--on' : '')} onClick={() => setView('live')}>
            Live demo
          </button>
        </nav>
        <div className="app__season muted small">
          <span className="app__season-name">Guest view</span>
          <button type="button" className="app__signout" onClick={onSignIn}>
            Sign in
          </button>
        </div>
      </header>

      <main className="app__body">
        {view === 'stats' && <Stats />}
        {view === 'live' && <GuestLiveDemo />}
      </main>
    </div>
  )
}

/**
 * A read-only live grid: the most recent finished game, shown exactly as the
 * scorer saw it, but nothing is tappable. It reuses the same StatGrid the live
 * screen uses, so a guest sees the real thing.
 */
function GuestLiveDemo() {
  const { games, players, currentSeason } = useApp()
  const { events, loading } = useAllEvents()

  const game = useMemo(
    () => [...games].filter((g) => g.status === 'final').sort((a, b) => b.date.localeCompare(a.date))[0],
    [games],
  )
  const stats = useMemo(() => orderStatTypes(currentSeason.enabledStatTypes), [currentSeason.enabledStatTypes])
  const gameEvents = useMemo(() => (game ? events.filter((e) => e.gameId === game.id) : []), [events, game])
  const counts = useMemo(() => tally(gameEvents), [gameEvents])
  const totals = useMemo(() => new Map(stats.map((id) => [id, teamTotal(gameEvents, id)] as const)), [stats, gameEvents])

  const rows = useMemo(() => {
    if (!game) return []
    return game.roster.map((id) => ({ id, name: players.find((p) => p.id === id)?.name ?? 'Player' }))
  }, [game, players])

  if (loading) return <div className="panel"><p className="muted">Loading the demo...</p></div>
  if (!game) return <div className="panel"><p className="muted">No finished games to demo yet.</p></div>

  return (
    <div className="live live--watch">
      <div className="panel" style={{ marginBottom: 8 }}>
        <p className="muted small">
          This is the live scoring grid, read-only — {game.homeAway === 'home' ? 'vs' : '@'} {game.opponentName} ·{' '}
          {game.roundAbbreviation ? `${game.roundAbbreviation} · ` : ''}
          {formatDate(game.date)}. During a real game the numbers tick up as the scorer taps, and everyone
          watching sees them live.
        </p>
      </div>
      <StatGrid rows={rows} stats={stats as StatTypeId[]} counts={counts} totals={totals} />
    </div>
  )
}
